import { Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React from 'react';
import ReactSelect from 'react-select';

import { unPadStoreNumber, sortStoresByNumber } from '../../utils/formatting';
import { storeViewsByCountry } from '../../utils/service-calls/reusable';
import countries from '../../utils/static/countriesWithNikeStores';

import ButtonWhite from './ButtonWhite';
import ToolTip from './ToolTip/ToolTip';

import './Select.css';

// Everything you need to run a storeSelect
const defaultStoreviewsFields = ['id', 'storeNumber', 'name', 'facilityType', 'company'];
const defaultStoreLabel = (store) => `${unPadStoreNumber(store.storeNumber)} - ${store.name} - ${store.facilityType} - ${store.company}`;
const defaultStoreHeaderLabel = 'Store: Number - Name - Type - Company';

class StoreSelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      fetching: false,
      storesInCountry: [],
    };
  }

  componentDidMount = () => {
    this.mounted = true;
    if (this.props.countryRestriction) {
      this.getStoresByCountry(this.props.countryRestriction);
    } else if (!this.props.noStores) {
      this.getStoresByCountry(this.props.selectedCountry);
    }
  };

  componentDidUpdate = (prevProps) => {
    if (
      this.props.countryRestriction
      && this.props.countryRestriction.value
      && this.props.countryRestriction.value !== (prevProps.countryRestriction && prevProps.countryRestriction.value)
    ) {
      this.getStoresByCountry(this.props.countryRestriction);
    }
  };

  componentWillUnmount = () => {
    this.mounted = false;
  };

  getStoreOptions = () => this.state.storesInCountry.map((storeInCountry) => ({
    full: storeInCountry,
    label: storeInCountry.label,
    value: storeInCountry.label,
  }));

getStoresByCountry = (country) => {
    if (!country.value) {
      return;
    }
    this.safeSetState({ errorMessage: '', fetching: true });
    let published = false;
    if (this.props.isPublished) {
      published = this.props.isPublished;
    }
    storeViewsByCountry(country.value, published, this.getStoreviewsFields())
      .then((res) => {
        if (!res.body || !res.body.objects || !Array.isArray(res.body.objects)) {
          throw new Error(res.statusText);
        }
        const storesInCountry = res.body.objects.map((storeInCountry) => ({
          ...storeInCountry,
          label: this.props.storeLabel(storeInCountry),
        }));
        sortStoresByNumber(storesInCountry);
        return this.safeSetState({
          fetching: false,
          storesInCountry: this.props.onlyNikeOwned
            ? storesInCountry.filter((store) => (store.facilityType === 'NIKE_OWNED_STORE'))
            : storesInCountry,
        });
      })
      .catch((err) => this.safeSetState({ errorMessage: err.message, fetching: false, storesInCountry: [] }));
  };

getStoreSelectValue = () => {
    if (this.props.isMulti) {
      return this.props.selectedStores.map((store) => ({
        full: store,
        label: store.label,
        value: store.label,
      }));
    }
    return this.props.selectedStore ? { label: this.props.selectedStore.label, value: this.props.selectedStore.label } : null;
  };

// to be pared down, yet.
getStoreviewsFields = () => (this.props.storeviewsFields ? [...new Set([...defaultStoreviewsFields, ...this.props.storeviewsFields])] : null);

// If a storeSelect is trying to use their own set of fields, make sure they have at least defaultStoreviewsFields the storeSelect itself works.
handleStoreSelectChange = (selectedOption) => {
    if (this.props.isMulti) {
      this.props.selectStore((selectedOption || []).map((option) => option.full));
      return;
    }
    this.props.selectStore(selectedOption.full);
  };

// If they don't pass their own storeviewsFields, then don't pass any so we still get all attributes from storeviews. They may not be ready for the results
isAtMaxSelections = () => (
    this.props.isMulti
    && this.props.maxSelections != null
    && this.props.selectedStores.length >= this.props.maxSelections
  );

safeSetState = (updates) => {
    if (this.mounted) {
      this.setState(updates);
    }
  };

  renderFullWidth = () => (
    <div style={{ width: '100%' }}>
      {!this.props.countryRestriction && (
        <div style={{ textAlign: 'left', width: '100%' }}>
          Country
          <ReactSelect
            classNamePrefix="react-select"
            clearable={false}
            id="countrySelect"
            isDisabled={this.props.isDisabled}
            options={countries}
            styles={{
              container: (styles) => ({ ...styles, width: '100%', zIndex: 110 }),
              control: (styles) => ({
                ...styles,
                borderColor: (this.props.isRequired && this.props.formErrors.selectedCountry) ? 'red' : 'lightgrey',
                padding: '0px 0px 0px 3px',
              }),
            }}
            value={this.props.selectedCountry}
            onChange={(selectedOption) => {
              if (!this.props.noStores) { this.getStoresByCountry(selectedOption); }
              this.props.selectCountry(selectedOption);
            }}
          />
        </div>
      )}
      {!this.props.noStores && (
        this.state.fetching
          ? <div><Loading /></div>
          : (
            <div style={{ textAlign: 'left' }}>
              <span>{this.props.storeHeaderLabel}</span>
              {this.props.isMulti && this.props.maxSelectionsMessage && (
                <ToolTip inline>{this.props.maxSelectionsMessage}</ToolTip>
              )}
              <ReactSelect
                classNamePrefix="react-select"
                clearable={false}
                closeMenuOnSelect={!this.props.isMulti}
                id="storeSelect"
                isDisabled={this.props.isDisabled || this.state.storesInCountry.length === 0}
                isMulti={this.props.isMulti}
                isOptionDisabled={() => this.isAtMaxSelections()}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                options={this.getStoreOptions()}
                placeholder={this.state.storesInCountry.length === 0 ? 'Select a country first...' : 'Select a store...'}
                styles={{
                  container: (styles) => ({ ...styles, zIndex: 100 }),
                  control: (styles) => ({
                    ...styles,
                    borderColor: (this.props.isRequired && this.props.formErrors.selectedStore) ? 'red' : 'lightgrey',
                    padding: '0px 0px 0px 3px',
                    ...(this.props.isMulti && { height: 'auto', minHeight: '40px' }),
                  }),
                  menuPortal: (styles) => ({ ...styles, zIndex: 9999 }),
                  valueContainer: (styles) => (this.props.isMulti
                    ? {
 ...styles, flexWrap: 'wrap', maxHeight: '160px', overflowY: 'auto'
}
                    : styles),
                }}
                value={this.getStoreSelectValue()}
                onChange={this.handleStoreSelectChange}
              />
            </div>
          )
      )}
      {this.state.errorMessage && <aside className="text-color-error body-4 mt2-sm">{this.state.errorMessage}</aside>}
    </div>
  );

  renderBody = () => (
    <>
      {!this.props.countryRestriction && (
        <article className={`ncss-col-sm-${this.props.columnWidth} va-sm-t ta-sm-l ${this.props.singleColumn ? 'm1-sm' : ''}`}>
          Country
          <ReactSelect
            classNamePrefix="react-select"
            clearable={false}
            id="countrySelect"
            isDisabled={this.props.isDisabled}
            options={countries}
            styles={{
              container: (styles) => ({ ...styles, zIndex: 110 }),
              control: (styles) => ({
                ...styles,
                borderColor: (this.props.isRequired && this.props.formErrors.selectedCountry) ? 'red' : 'lightgrey',
                padding: '0px 0px 0px 3px',
              }),
            }}
            value={this.props.selectedCountry}
            onChange={(selectedOption) => {
              if (!this.props.noStores) { this.getStoresByCountry(selectedOption); }
              this.props.selectCountry(selectedOption);
            }}
          />
          {this.props.isRequired && this.props.formErrors.selectedCountry
            && <p className="text-color-error body-4 mt2-sm">{this.props.formErrors.selectedCountry}</p>}
        </article>
      )}
      {!this.props.noStores && (
        this.state.fetching
          ? <article className={`ncss-col-sm-${this.props.columnWidth} va-sm-t mt-6-sm`}><Loading /></article>
          : (
            <article className={`ncss-col-sm-${this.props.columnWidth} va-sm-t ta-sm-l`}>
              <span>{this.props.storeHeaderLabel}</span>
              {this.props.isMulti && this.props.maxSelectionsMessage && (
                <ToolTip inline>{this.props.maxSelectionsMessage}</ToolTip>
              )}
              <ReactSelect
                classNamePrefix="react-select"
                clearable={false}
                closeMenuOnSelect={!this.props.isMulti}
                id="storeSelect"
                isDisabled={this.props.isDisabled || this.state.storesInCountry.length === 0}
                isMulti={this.props.isMulti}
                isOptionDisabled={() => this.isAtMaxSelections()}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                options={this.getStoreOptions()}
                placeholder={this.state.storesInCountry.length === 0 ? 'Select a country first...' : 'Select a store...'}
                styles={{
                  container: (styles) => ({ ...styles, zIndex: 100 }),
                  control: (styles) => ({
                    ...styles,
                    borderColor: (this.props.isRequired && this.props.formErrors.selectedStore) ? 'red' : 'lightgrey',
                    padding: '0px 0px 0px 3px',
                    ...(this.props.isMulti && { height: 'auto', minHeight: '40px' }),
                  }),
                  menuPortal: (styles) => ({ ...styles, zIndex: 9999 }),
                  valueContainer: (styles) => (this.props.isMulti
                    ? {
 ...styles, flexWrap: 'wrap', maxHeight: '160px', overflowY: 'auto'
}
                    : styles),
                }}
                value={this.getStoreSelectValue()}
                onChange={this.handleStoreSelectChange}
              />
              {this.props.isRequired && this.props.formErrors.selectedStore
                && <p className="text-color-error body-4 mt2-sm">{this.props.formErrors.selectedStore}</p>}
            </article>
          )
      )}
      {this.state.errorMessage && <aside className="text-color-error body-4 mt2-sm">{this.state.errorMessage}</aside>}
      {!this.props.isMulti && this.props.selectedStores.length !== 0 && (
        <section>
          <article className="ncss-col-sm-8 mt2-sm ta-sm-l">
            <p className="text-color-accent">Selected Stores:</p>
            <ul>
              {this.props.selectedStores.map((store) => <li key={`${store.storeNumber}`}>{`*  ${store.label}`}</li>)}
            </ul>
          </article>
          <article className="ncss-col-sm-4 mt4-sm ta-sm-r va-sm-t">
            <ButtonWhite className="border" label="Clear Stores" onClick={this.props.onClear} />
          </article>
        </section>
        )}
    </>
  );

  render = () => {
    if (this.props.fullWidth) {
      return this.renderFullWidth();
    }
    if (this.props.inline) {
      return this.renderBody();
    }
    return <section className="ncss-row full-width">{this.renderBody()}</section>;
  };
}

StoreSelect.defaultProps = {
  columnWidth: 6,
  countryRestriction: null,
  formErrors: {},
  fullWidth: false,
  inline: false,
  isDisabled: false,
  isMulti: false,
  isPublished: false,
  isRequired: false,
  maxSelections: null,
  maxSelectionsMessage: null,
  noStores: false,
  onClear: () => null,
  onlyNikeOwned: false,
  selectedCountry: {},
  selectedStore: {},
  selectedStores: [],
  singleColumn: false,
  storeHeaderLabel: defaultStoreHeaderLabel,
  storeLabel: defaultStoreLabel,
  storeviewsFields: null,
};

StoreSelect.propTypes = {
  // ncss grid width (out of 12) applied to the Country and Store articles. Default 6
  // keeps the historical two-column layout; set lower (e.g. 4) when composing with
  // additional fields in the same parent row.
  columnWidth: PropTypes.number,
  countryRestriction: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  formErrors: PropTypes.shape(),
  fullWidth: PropTypes.bool,
  // When true, StoreSelect skips its own `<section className="ncss-row full-width">`
  // wrapper so Country/Store articles become direct children of the parent row.
  inline: PropTypes.bool,
  isDisabled: PropTypes.bool,
  // When true, the store dropdown becomes a multi-select backed by `selectedStores`.
  // `selectStore` is then called with the full array of selected store objects, not a single one.
  isMulti: PropTypes.bool,
  isPublished: PropTypes.bool,
  isRequired: PropTypes.bool,
  // Only applied in isMulti mode: once selectedStores.length reaches this value,
  // further options are rendered disabled by react-select's isOptionDisabled hook.
  maxSelections: PropTypes.number,
  maxSelectionsMessage: PropTypes.string,
  noStores: PropTypes.bool,
  onClear: PropTypes.func,
  onlyNikeOwned: PropTypes.bool,
  selectCountry: PropTypes.func.isRequired,
  selectedCountry: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  selectedStore: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  selectedStores: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })),
  selectStore: PropTypes.func.isRequired,
  singleColumn: PropTypes.bool,
  // Header text rendered above the store dropdown. Pair with `storeLabel` when
  // the dropdown shows a different field set than the default.
  storeHeaderLabel: PropTypes.string,
  // Function that builds the option label shown in the store dropdown.
  // Receives the raw store object (with `storeNumber`, `name`, `facilityType`, `company`, ...).
  storeLabel: PropTypes.func,
  storeviewsFields: PropTypes.arrayOf(PropTypes.string),
};

export default StoreSelect;
